import { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { InfoCircleOutlined, LinkOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  message,
  Popconfirm,
  Popover,
  Segmented,
  Space,
  Switch,
  Table,
  Tabs,
  Tooltip,
} from "antd";
import camelCase from "lodash.camelcase";
import { parseAsString, parseAsStringEnum, useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { RequestStatus, type AppStore, type ProductWithStats } from "@fitness-app/app-store";
import { productActions } from "@fitness-app/app-store/src/store/reducers/product";
import { type ProductAutomation } from "@fitness-app/data-models/entities/Automation";
import {
  PaymentClient,
  ProductChargeType,
  ProductStatus,
  type Product,
} from "@fitness-app/data-models/entities/Product";
import { getProductLink } from "@fitness-app/utils/src/products/getProductLink";

import ModalForm from "~/components/ModalForm/ModalForm";
import {
  DEFAULT_STRIPE_ACCOUNT_KEY,
  DEFAULT_STRIPE_ACCOUNT_LABEL,
  getProductStripeAccountKey,
} from "~/modules/Products/helpers/productStripeAccount";
import { useDuplicateProduct, type DuplicateProductFormModel } from "~/modules/Products/hooks/useDuplicateProduct";
import { useProductSubmit } from "~/modules/Products/hooks/useProductSubmit";
import ProductPrices from "~/modules/Products/Product/ProductPrices/ProductPrices";
import { type ProductFormModel } from "~/modules/Products/ProductForm/types";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import DuplicateProductForm from "../ProductForm/DuplicateProductForm";
import { usePaymentGatewayOptions } from "../ProductForm/hooks/usePaymentGatewayOptions";
import ProductForm from "../ProductForm/ProductForm";

interface ProductsTableProps {
  dataSource: ProductWithStats[];
  loading: boolean;
  t: ReturnType<typeof useTranslation<["products", "common", "subscription"]>>["t"];
  stripeOptionsByKey: Record<string, { name: string; accountId: string; isDefault: boolean }>;
  tpayOptionsByKey: Record<string, { posId: string; isDefault?: boolean }>;
  dispatch: ReturnType<typeof useAppDispatch>;
  productDetailsStatus: RequestStatus | null;
  productId: string | null;
  handleArchiveProduct: (id: string) => Promise<void>;
  handleRemoveProduct: (id: string) => Promise<void>;
  toggleProductForm: (show: boolean) => void;
  onDuplicateProduct: (product: ProductWithStats) => void;
}

const ProductsTable: FunctionComponent<ProductsTableProps> = ({
  dataSource,
  loading,
  t,
  stripeOptionsByKey,
  tpayOptionsByKey,
  dispatch,
  productDetailsStatus,
  productId,
  handleArchiveProduct,
  handleRemoveProduct,
  toggleProductForm,
  onDuplicateProduct,
}) => (
  <Table
    loading={loading}
    dataSource={dataSource}
    scroll={{ x: true }}
    expandable={{
      expandedRowRender: (rowData) => (
        <ProductPrices
          prices={rowData.prices}
          productId={rowData.id}
          productName={rowData.name}
          productSlug={rowData.slug || ""}
        />
      ),
      rowExpandable: (row) => row.chargeType !== ProductChargeType.FREE && row.status === ProductStatus.ACTIVE,
    }}
    rowKey="id"
    pagination={{
      hideOnSinglePage: true,
      pageSize: 100,
    }}
    locale={{
      emptyText: (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t<string>("common:emptyList")}>
          <Tooltip title={null}>
            <Button icon={<PlusOutlined />} onClick={() => toggleProductForm(true)} type="primary" className="mt-2">
              {t("addNewProduct")}
            </Button>
          </Tooltip>
        </Empty>
      ),
    }}
  >
    <Table.Column<ProductWithStats>
      title={t("list.columns.name")}
      dataIndex="name"
      key="name"
      render={(name, row) => (
        // TRE-2076: min-w, żeby nazwa na telefonie nie łamała się do 4 linii obok miniatury.
        <span className="flex min-w-[220px] items-center gap-3 lg:gap-6">
          {row.images?.length ? <img alt="product" src={row.images[0]} className="h-auto max-w-[50px]" /> : null}
          <h4 className="mt-2">{name}</h4>
        </span>
      )}
    />
    <Table.Column<ProductWithStats>
      title={t("list.columns.description")}
      dataIndex="description"
      key="description"
      responsive={["md"]}
      render={(rowText: string) =>
        rowText ? (
          <Popover content={rowText} overlayStyle={{ maxWidth: 300 }}>
            <InfoCircleOutlined style={{ fontSize: 20 }} />
          </Popover>
        ) : null
      }
    />
    <Table.Column<ProductWithStats>
      title={t("list.columns.paymentType")}
      dataIndex="chargeType"
      key="chargeType"
      responsive={["md"]}
      render={(_, row) => t(`chargeType.${row.chargeType === ProductChargeType.FREE ? "free" : "paid"}`)}
    />
    <Table.Column<ProductWithStats>
      title="Konto Stripe / Tpay"
      dataIndex="account"
      responsive={["lg"]}
      key="account"
      render={(_, row) => {
        const stripeAccount = row.customStripeAccount ? stripeOptionsByKey[row.customStripeAccount] : null;
        const tpayAccount = row.tpayIntegration ? tpayOptionsByKey[row.tpayIntegration] : null;

        let stripeLabel = stripeAccount && !stripeAccount.isDefault ? `${stripeAccount.name}` : "Główne";
        let tpayLabel = tpayAccount && !tpayAccount.isDefault ? `${tpayAccount.posId}` : "Główne";

        if (row.enabledPaymentGateways && row.enabledPaymentGateways?.length > 0) {
          if (!row.enabledPaymentGateways.includes(PaymentClient.STRIPE)) {
            stripeLabel = "Nieaktywne";
          }

          if (!row.enabledPaymentGateways.includes(PaymentClient.TPAY)) {
            tpayLabel = "Nieaktywne";
          }
        }

        if (stripeLabel === "Główne" && tpayLabel === "Główne") {
          return "Główne";
        }

        return (
          <span className="whitespace-nowrap">
            {stripeLabel} / {tpayLabel}
          </span>
        );
      }}
    />
    <Table.Column<ProductWithStats>
      title={t("list.columns.type")}
      dataIndex="type"
      key="type"
      responsive={["lg"]}
      /* eslint-disable-next-line */
      render={(type) => t(`productType.${camelCase(type)}`)}
    />
    <Table.Column<ProductWithStats>
      title={t("list.columns.totalClients")}
      dataIndex="totalClients"
      key="totalClients"
      render={(_, row) => {
        // Aktywne (z dostępem) / wszystkie — dla darmowych produktów nie ma aktywnych, zostaje suma.
        const total = row.product_stats?.totalClients ?? 0;
        if (row.chargeType === ProductChargeType.FREE) {
          return total;
        }
        return (
          <Tooltip title={`${t("productDetails.activeClientsDesc")} / ${t("productDetails.totalClients")}`}>
            <span className="whitespace-nowrap">
              {row.product_stats?.activeClients ?? 0} / {total}
            </span>
          </Tooltip>
        );
      }}
    />
    <Table.Column<ProductWithStats>
      title={t("list.columns.link")}
      dataIndex="link"
      key="link"
      responsive={["md"]}
      render={(_, row) => (
        <Tooltip title={t("list.copyToClipboard")}>
          <LinkOutlined
            style={{ fontSize: 22 }}
            onClick={() =>
              navigator.clipboard.writeText(
                getProductLink({
                  productId: row.id,
                  domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
                  slug: row.slug || "",
                }),
              )
            }
          />
        </Tooltip>
      )}
    />
    <Table.Column<Product>
      title={t("list.columns.active")}
      dataIndex="active"
      key="active"
      render={(active: boolean, row) => (
        <Switch
          checked={row.status === ProductStatus.ACTIVE}
          onChange={() =>
            dispatch(
              productActions.updateProduct({
                payload: {
                  id: row.id,
                  status: row.status === ProductStatus.ACTIVE ? ProductStatus.INACTIVE : ProductStatus.ACTIVE,
                  description: row.description ?? "",
                  images: row.images,
                  name: row.name,
                },
              }),
            )
          }
          loading={productDetailsStatus === RequestStatus.UPDATING && productId === row.id}
        />
      )}
    />
    <Table.Column<ProductWithStats>
      title={t("list.columns.options")}
      key="action"
      align="left"
      render={({ id }, row) => (
        <Space>
          <Link to={`/products/${id}`}>{t("list.columns.details")}</Link>
          <Tooltip title={t<string>("products:duplicate.tooltip", "Duplikuj produkt")}>
            <Button type="link" onClick={() => onDuplicateProduct(row)}>
              {t<string>("products:duplicate.action", "Duplikuj")}
            </Button>
          </Tooltip>
          {row.product_stats?.totalClients ? (
            <Popconfirm
              overlayStyle={{ maxWidth: 400 }}
              placement="leftTop"
              title={t("mailing.archiveWarning")}
              onConfirm={() => handleArchiveProduct(row.id)}
            >
              <Button type="link" danger>
                {t("common:button.archive")}
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              overlayStyle={{ maxWidth: 400 }}
              placement="leftTop"
              title={t("mailing.deleteWarning")}
              onConfirm={() => handleRemoveProduct(row.id)}
            >
              <Button type="link" danger>
                {t("common:button.delete")}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )}
    />
  </Table>
);

const ProductsList: FunctionComponent = () => {
  const { t } = useTranslation(["products", "common", "subscription"]);
  const [showProductForm, toggleProductForm] = useState(false);
  const { addingNewProduct, duplicatingProduct, productsListStatus, productsListData } = useAppSelector(
    (store) => store.products,
  );
  const { productId, productDetailsStatus } = useAppSelector((store: AppStore) => store.product);
  const dispatch = useAppDispatch();
  const { tpayOptionsByKey, stripeOptionsByKey } = usePaymentGatewayOptions();
  const [viewMode, setViewMode] = useQueryState(
    "view",
    parseAsStringEnum(["list", "stripeAccounts"]).withDefault("list"),
  );
  const [stripeAccountTab, setStripeAccountTab] = useQueryState(
    "account",
    parseAsString.withDefault(DEFAULT_STRIPE_ACCOUNT_KEY),
  );
  const [productToDuplicate, setProductToDuplicate] = useState<ProductWithStats | null>(null);
  const [duplicateAutomationCounts, setDuplicateAutomationCounts] = useState<
    { tasks: number; webhooks: number } | undefined
  >(undefined);

  useEffect(() => {
    if (!productToDuplicate) {
      setDuplicateAutomationCounts(undefined);
      return;
    }

    let cancelled = false;
    void supabase
      .from("product_automation")
      .select("tasks,webhooks")
      .eq("productId", productToDuplicate.id)
      .maybeSingle<Pick<ProductAutomation, "tasks" | "webhooks">>()
      .then(({ data }) => {
        if (cancelled) return;
        setDuplicateAutomationCounts({
          tasks: data?.tasks?.length ?? 0,
          webhooks: data?.webhooks?.length ?? 0,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [productToDuplicate]);

  const productsByStripeAccount = useMemo(() => {
    const groups: Record<string, { label: string; products: ProductWithStats[] }> = {};

    groups[DEFAULT_STRIPE_ACCOUNT_KEY] = { label: DEFAULT_STRIPE_ACCOUNT_LABEL, products: [] };

    for (const key of Object.keys(stripeOptionsByKey)) {
      const account = stripeOptionsByKey[key];
      if (account && !account.isDefault) {
        groups[key] = { label: account.name, products: [] };
      }
    }

    for (const product of productsListData) {
      const accountKey = getProductStripeAccountKey(product, stripeOptionsByKey);

      if (!groups[accountKey]) {
        groups[accountKey] = { label: accountKey, products: [] };
      }
      groups[accountKey].products.push(product);
    }

    return groups;
  }, [productsListData, stripeOptionsByKey]);

  const handleArchiveProduct = async (productId: string) => {
    void message.loading(t("messages.loading.archiveProduct"));

    const onSuccess = () => {
      void message.success(t("messages.success.archiveProduct"));
    };
    const onFailure = () => {
      void message.success(t("messages.error.archiveProduct"));
    };
    await dispatch(
      productActions.archiveProduct({
        payload: { id: productId },
        onFailure,
        onSuccess,
      }),
    );
  };

  const handleRemoveProduct = async (productId: string) => {
    void message.loading({
      content: t("messages.loading.removeProduct"),
      duration: 0,
      key: "removeProduct",
    });

    const onSuccess = () => {
      message.destroy("removeProduct");
      void message.success(t("messages.success.removeProduct"));
    };
    const onFailure = () => {
      message.destroy("removeProduct");
      void message.error(t("messages.error.removeProduct"));
    };
    await dispatch(
      productActions.removeProduct({
        payload: { id: productId },
        onFailure,
        onSuccess,
      }),
    );
  };

  const { handleSubmit: onAddNewProduct } = useProductSubmit(() => {
    toggleProductForm(false);
  });

  const { handleSubmit: onDuplicateSubmit } = useDuplicateProduct(() => {
    setProductToDuplicate(null);
  });

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={24}>
      <Card
        bordered={false}
        loading={productsListStatus === RequestStatus.SUBSCRIBING}
        extra={
          productsListData.length ? (
            <Tooltip title={null}>
              <Button type="primary" onClick={() => toggleProductForm(true)} icon={<PlusOutlined />}>
                {t<string>("products:button.addProduct")}
              </Button>
            </Tooltip>
          ) : null
        }
      >
        <Segmented
          value={viewMode}
          onChange={(value) => setViewMode(value as "list" | "stripeAccounts")}
          options={[
            { label: "Lista", value: "list" },
            { label: "Konta Stripe", value: "stripeAccounts" },
          ]}
          className="mb-4"
        />
        {viewMode === "stripeAccounts" ? (
          <Tabs
            activeKey={
              productsByStripeAccount[stripeAccountTab]?.products.length ? stripeAccountTab : DEFAULT_STRIPE_ACCOUNT_KEY
            }
            onChange={(key) => setStripeAccountTab(key)}
            items={Object.entries(productsByStripeAccount)
              .filter(([, group]) => group.products.length > 0)
              .map(([key, group]) => ({
                key,
                label: `${group.label} (${group.products.length})`,
                children: (
                  <ProductsTable
                    dataSource={group.products}
                    loading={productsListStatus === RequestStatus.SUBSCRIBING}
                    t={t}
                    stripeOptionsByKey={stripeOptionsByKey}
                    tpayOptionsByKey={tpayOptionsByKey}
                    dispatch={dispatch}
                    productDetailsStatus={productDetailsStatus}
                    productId={productId}
                    handleArchiveProduct={handleArchiveProduct}
                    handleRemoveProduct={handleRemoveProduct}
                    toggleProductForm={toggleProductForm}
                    onDuplicateProduct={setProductToDuplicate}
                  />
                ),
              }))}
          />
        ) : (
          <ProductsTable
            dataSource={productsListData}
            loading={productsListStatus === RequestStatus.SUBSCRIBING}
            t={t}
            stripeOptionsByKey={stripeOptionsByKey}
            tpayOptionsByKey={tpayOptionsByKey}
            dispatch={dispatch}
            productDetailsStatus={productDetailsStatus}
            productId={productId}
            handleArchiveProduct={handleArchiveProduct}
            handleRemoveProduct={handleRemoveProduct}
            toggleProductForm={toggleProductForm}
            onDuplicateProduct={setProductToDuplicate}
          />
        )}
        <ModalForm<ProductFormModel>
          visible={showProductForm}
          onCancel={() => toggleProductForm(false)}
          title={t<string>("products:addNewProduct")}
          loading={addingNewProduct}
          width={900}
        >
          <ProductForm onSubmit={onAddNewProduct} />
        </ModalForm>
        {productToDuplicate ? (
          <ModalForm<DuplicateProductFormModel>
            visible
            onCancel={() => setProductToDuplicate(null)}
            title={t<string>("products:duplicate.modalTitle", "Duplikuj produkt")}
            loading={duplicatingProduct}
            width={720}
          >
            <DuplicateProductForm
              source={productToDuplicate}
              automationCounts={duplicateAutomationCounts}
              onSubmit={onDuplicateSubmit}
            />
          </ModalForm>
        ) : null}
      </Card>
    </Space>
  );
};

export default ProductsList;
